import { create } from 'zustand'
import {
    managerSuggestionService,
    type PotentialManager,
    type GroupedManagerResponse,
    type PotentialManagersParams,
} from '../services/managerSuggestionService'

interface ManagerSuggestionState {
    potentialManagers: PotentialManager[]
    groupedManagers: GroupedManagerResponse | null
    suggestedManager: PotentialManager | null
    isLoading: boolean
    error: string | null

    fetchPotentialManagers: (params: PotentialManagersParams) => Promise<void>
    fetchSuggestedManager: (scopeInstanceId: string, employeeId?: string) => Promise<void>
    reset: () => void
}

export const useManagerSuggestionStore = create<ManagerSuggestionState>((set) => ({
    potentialManagers: [],
    groupedManagers: null,
    suggestedManager: null,
    isLoading: false,
    error: null,

    async fetchPotentialManagers(params) {
        set({ isLoading: true, error: null })

        try {
            const data = await managerSuggestionService.getPotentialManagers(params)

            // Check if response is grouped or flat
            if (params.grouped && 'groups' in data) {
                set({
                    groupedManagers: data as GroupedManagerResponse,
                    suggestedManager: (data as GroupedManagerResponse).suggested,
                    potentialManagers: [],
                    isLoading: false,
                })
            } else {
                set({
                    potentialManagers: data as PotentialManager[],
                    groupedManagers: null,
                    suggestedManager: (data as PotentialManager[])[0] || null,
                    isLoading: false,
                })
            }
        } catch (error) {
            console.error('Failed to fetch potential managers', error)
            set({
                error: error instanceof Error ? error.message : 'Unable to load potential managers',
                isLoading: false,
            })
            throw error
        }
    },

    async fetchSuggestedManager(scopeInstanceId, employeeId) {
        set({ isLoading: true, error: null })

        try {
            const data = await managerSuggestionService.getSuggestedManager(scopeInstanceId, employeeId)
            set({
                suggestedManager: data.suggested,
                isLoading: false,
            })
        } catch (error) {
            console.error('Failed to fetch suggested manager', error)
            set({
                error: error instanceof Error ? error.message : 'Unable to load suggested manager',
                isLoading: false,
            })
            throw error
        }
    },

    reset() {
        set({
            potentialManagers: [],
            groupedManagers: null,
            suggestedManager: null,
            isLoading: false,
            error: null,
        })
    },
}))
